import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { formatSqYd, formatDate } from "@/lib/statusUtils";
import { DIRECTION_LABEL } from "@/lib/portionUtils";
import { CheckCircle2, XCircle, ClipboardCheck, Inbox } from "lucide-react";

export default function AdminPendingPortions() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rejecting, setRejecting] = useState(null);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/plots/pending-portions/list");
      setRows(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const decide = async (portion, action, reasonText) => {
    setSubmitting(true);
    try {
      await api.post(`/plots/${portion.plot_id}/portions/${portion.id}/decision`, {
        action,
        reason: reasonText || null,
      });
      toast.success(`Portion ${action}d`);
      setRejecting(null);
      setReason("");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const grouped = useMemo(() => {
    const by = new Map();
    for (const r of rows) {
      const key = r.plot_id;
      if (!by.has(key)) by.set(key, { plot_no: r.plot_no, lp_no: r.lp_no, phase_name: r.phase_name, master_total_sqyd: r.master_total_sqyd, portions: [] });
      by.get(key).portions.push(r);
    }
    return Array.from(by.values());
  }, [rows]);

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-12 py-6 md:py-8">
        <div className="flex items-end justify-between mb-6 gap-3 flex-wrap">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#C06C47] font-semibold mb-2">Partial Portion Approvals</div>
            <h1 className="font-heading text-2xl md:text-4xl">
              {isSuperAdmin ? "Pending Super Admin Approval" : "My Pending Partial Portions"}
            </h1>
            <p className="text-sm text-[#5C6653] mt-2">
              {isSuperAdmin
                ? "Admin-created partial portions waiting for your review. Approving makes the portion bookable; rejecting requires a reason."
                : "Partial portions you have created and submitted for Super Admin approval. Once approved they become bookable."}
            </p>
          </div>
          <div className="text-xs text-[#5C6653]" data-testid="pending-count">
            <span className="font-mono-anu text-[#1A1F16] text-lg font-medium">{rows.length}</span> pending portion(s)
          </div>
        </div>

        {loading ? (
          <div className="text-center text-[#5C6653] py-16">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="border border-[#E5E2DC] bg-white p-10 text-center" data-testid="pending-empty">
            <Inbox className="h-8 w-8 mx-auto text-[#5C6653] mb-3" />
            <div className="font-heading text-lg text-[#1A1F16] mb-1">All caught up</div>
            <div className="text-sm text-[#5C6653]">No partial portions are waiting on Super Admin approval right now.</div>
          </div>
        ) : (
          <div className="space-y-5">
            {grouped.map((g) => (
              <div key={`${g.plot_no}-${g.lp_no}`} className="border border-[#E5E2DC] bg-white" data-testid={`pending-plot-${g.plot_no}-${(g.lp_no||"").replace(/\//g,"-")}`}>
                <div className="px-4 py-3 border-b border-[#E5E2DC] bg-[#F0EFEA] flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <div className="text-[10px] tracking-[0.2em] uppercase text-[#5C6653] font-semibold">{g.phase_name} · LP {g.lp_no}</div>
                    <div className="font-heading text-lg">Plot #{g.plot_no}</div>
                  </div>
                  <div className="text-xs text-[#5C6653]">Total area: <span className="font-mono-anu text-[#1A1F16]">{formatSqYd(g.master_total_sqyd)}</span></div>
                </div>
                <div className="divide-y divide-[#E5E2DC]">
                  {g.portions.map((p) => (
                    <div key={p.id} className="px-4 py-3 flex items-start gap-4 flex-wrap" data-testid={`pending-portion-${p.portion_code}`}>
                      <div className="flex-1 min-w-[180px]">
                        <div className="font-mono-anu text-xs text-[#5C6653]">{p.portion_code}</div>
                        <div className="font-medium text-sm">
                          {DIRECTION_LABEL[p.direction] || p.direction} · {formatSqYd(p.area_sqyd)}
                        </div>
                        <div className="text-[11px] text-[#5C6653] mt-0.5">Submitted {formatDate(p.created_at)}</div>
                      </div>
                      <div className="text-xs text-[#5C6653] max-w-sm">
                        {p.boundaries && (
                          <div className="space-y-0.5">
                            {["north", "south", "east", "west"].map((s) => (
                              p.boundaries?.[s] ? (
                                <div key={s}><span className="uppercase tracking-wider text-[10px] mr-1">{s.charAt(0)}:</span>{p.boundaries[s]}</div>
                              ) : null
                            ))}
                          </div>
                        )}
                        {p.road_facing && <div className="mt-1"><span className="uppercase tracking-wider text-[10px] mr-1">Road:</span>{p.road_facing}</div>}
                      </div>
                      {isSuperAdmin ? (
                        <div className="flex gap-2">
                          <Button
                            type="button" size="sm"
                            className="rounded-sm bg-[#234B36] hover:bg-[#1A3A29] text-white h-8 text-xs"
                            onClick={() => decide(p, "approve")}
                            disabled={submitting}
                            data-testid={`approve-portion-${p.portion_code}`}
                          >
                            <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Approve
                          </Button>
                          <Button
                            type="button" size="sm" variant="outline"
                            className="rounded-sm h-8 text-xs border-[#DC2626] text-[#DC2626] hover:bg-[#DC2626]/10"
                            onClick={() => { setRejecting(p); setReason(""); }}
                            disabled={submitting}
                            data-testid={`reject-portion-${p.portion_code}`}
                          >
                            <XCircle className="h-3.5 w-3.5 mr-1" /> Reject
                          </Button>
                        </div>
                      ) : (
                        <span className="text-[10px] px-2 py-1 border font-medium tracking-wider uppercase whitespace-nowrap"
                          style={{ background: "#EAB30820", borderColor: "#B08B00", color: "#B08B00" }}
                          data-testid={`pending-badge-${p.portion_code}`}
                        >
                          <ClipboardCheck className="inline h-3 w-3 mr-1 -mt-0.5" />
                          Awaiting Super Admin
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={!!rejecting} onOpenChange={(o) => !o && setRejecting(null)}>
        <DialogContent className="max-w-md rounded-sm" data-testid="reject-portion-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Reject Partial Portion</DialogTitle>
            <DialogDescription className="text-xs text-[#5C6653]">
              {rejecting && (
                <>Reject <span className="font-mono-anu">{rejecting.portion_code}</span>? Please provide a reason for audit.</>
              )}
            </DialogDescription>
          </DialogHeader>
          <Textarea
            rows={3} required
            placeholder="Reason for rejection…"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            data-testid="reject-portion-reason"
            className="rounded-sm"
          />
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => setRejecting(null)} className="rounded-sm" data-testid="reject-portion-cancel">Cancel</Button>
            <Button
              type="button"
              className="rounded-sm bg-[#DC2626] hover:bg-[#B91C1C] text-white"
              disabled={submitting || !reason.trim()}
              onClick={() => decide(rejecting, "reject", reason.trim())}
              data-testid="reject-portion-confirm"
            >
              {submitting ? "Rejecting…" : "Reject portion"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
